import { useMemo } from 'react';
import { Feature, MultiLineString, MultiPolygon, Polygon } from 'geojson';
import { Layer, LayerProps, Source } from 'shared/map-exports';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { CrossHatchPatternBlack } from 'features/mapping/icons';

const FILL_SOURCE_ID = 'editable-fire-perimeter';
const EXTERIOR_STROKE_SOURCE_ID = 'editable-fire-perimeter-exterior';
const CONTRACTION_SOURCE_ID = 'editable-fire-perimeter-contraction';

const FILL_LAYER_ID = 'editable-fire-perimeter-fill';
const STROKE_LAYER_ID = 'editable-fire-perimeter-stroke';
const EXTERIOR_STROKE_LAYER_ID = 'editable-fire-perimeter-stroke-exterior';
const CONTRACTION_LAYER_ID = 'editable-fire-perimeter-contraction-fill';

type EditableFirePerimeterLayerProps = {
  geom: Polygon | MultiPolygon | null;
  /** Fill/stroke color reflecting the perimeter's (pending) approval status and type. */
  color: string;
  /** New area (pending minus overlapping approved); rendered instead of `geom` when set. */
  activePerimetersDelta?: Polygon | MultiPolygon | null;
  /** Delta's exterior boundary, stroked separately so the shared inner edge stays clean. */
  activePerimetersDeltaStroke?: MultiLineString | null;
  activePerimetersContraction?: Polygon | MultiPolygon | null;
};

const contractionFillStyle: LayerProps = {
  type: 'fill',
  paint: {
    'fill-pattern': CrossHatchPatternBlack.name,
    'fill-opacity': 0.6,
  },
};

const getFillStyle = (color: string): LayerProps => ({
  type: 'fill',
  paint: {
    'fill-color': color,
    'fill-opacity': 0.6,
  },
});

const getStrokeStyle = (color: string): LayerProps => ({
  type: 'line',
  paint: {
    'line-color': color,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 18, 4],
  },
});

/**
 * Renders a single fire perimeter from backend geometry, independent of the
 * `fireperimeters_active` tileset. With a delta, fills only the new area and
 * strokes its exterior boundary.
 */
export const EditableFirePerimeterLayer = (
  props: EditableFirePerimeterLayerProps,
): JSX.Element | null => {
  const {
    geom,
    color,
    activePerimetersDelta,
    activePerimetersDeltaStroke,
    activePerimetersContraction,
  } = props;

  const displayGeom = activePerimetersDelta ?? geom;

  const fillData = useMemo<Feature | null>(
    () =>
      displayGeom
        ? { type: 'Feature', geometry: displayGeom, properties: {} }
        : null,
    [displayGeom],
  );

  const strokeData = useMemo<Feature | null>(
    () =>
      activePerimetersDeltaStroke
        ? {
            type: 'Feature',
            geometry: activePerimetersDeltaStroke,
            properties: {},
          }
        : null,
    [activePerimetersDeltaStroke],
  );

  const contractionData = useMemo<Feature | null>(
    () =>
      activePerimetersContraction
        ? {
            type: 'Feature',
            geometry: activePerimetersContraction,
            properties: {},
          }
        : null,
    [activePerimetersContraction],
  );

  if (!fillData) return null;

  return (
    <>
      <Source id={FILL_SOURCE_ID} type="geojson" data={fillData}>
        <Layer
          id={FILL_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...getFillStyle(color)}
        />
        {/* With a delta, the exterior is stroked separately below. */}
        {!activePerimetersDelta && (
          <Layer
            id={STROKE_LAYER_ID}
            beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
            {...getStrokeStyle(color)}
          />
        )}
      </Source>
      {strokeData && (
        <Source id={EXTERIOR_STROKE_SOURCE_ID} type="geojson" data={strokeData}>
          <Layer
            id={EXTERIOR_STROKE_LAYER_ID}
            beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
            {...getStrokeStyle(color)}
          />
        </Source>
      )}
      {contractionData && (
        <Source
          id={CONTRACTION_SOURCE_ID}
          type="geojson"
          data={contractionData}
        >
          <Layer
            id={CONTRACTION_LAYER_ID}
            beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
            {...contractionFillStyle}
          />
        </Source>
      )}
    </>
  );
};
